/**
 * Aviso "N listas apagadas" (ver deleteToast.ts), montado uma vez no AppShell.
 *
 * Movimento (23/10/2026): toast do guia (.ds-toast com .is-aberto, pelo usePresenca). Sobe 8 px
 * com fade em 200 ms e sai em 140 ms. A barra fina (.ds-toast-tempo) mostra quanto falta dos 4 s
 * (--permanencia-4000; não há Desfazer) e para com o mouse em cima, junto com o tempo de verdade.
 * Um por vez: um aviso novo troca o texto no lugar (fade) e recomeça a barra.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePresenca } from '../../hooks/usePresenca';
import { useDeleteToastStore } from './deleteToast';
import { PERMANENCIA_4000, useUltimoAberto } from './movimentoListas';
import './deleteDialog.css';

const SHOW_MS = PERMANENCIA_4000;

const hide = (): void => useDeleteToastStore.setState({ message: null });

export function DeleteToastHost(): JSX.Element | null {
  const message = useDeleteToastStore((s) => s.message);
  const stamp = useDeleteToastStore((s) => s.stamp);
  const icon = useDeleteToastStore((s) => s.icon);
  const open = Boolean(message);
  const { montado, aberto } = usePresenca(open, '--duracao-140');
  // Saindo, continua o último aviso.
  const shown = useUltimoAberto(open, { message, icon, stamp });

  // O primeiro aviso entra com o toast; os seguintes, com ele já aberto, trocam no lugar.
  const [firstStamp, setFirstStamp] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!open) setFirstStamp(null);
    else setFirstStamp((first) => first ?? stamp);
  }, [open, stamp]);

  // Tempo que falta, parado com o mouse em cima (como a barra).
  const timer = useRef<number>();
  const left = useRef(SHOW_MS);
  const startedAt = useRef(0);
  // Mouse em cima: um aviso novo que chega assim fica parado até o mouse sair (a barra nova
  // também fica cheia pelo :hover), sem um mouseenter novo.
  const over = useRef(false);

  useEffect(() => {
    if (!message) return undefined;
    left.current = SHOW_MS;
    startedAt.current = Date.now();
    // O tempo de antes (inclusive o que o mouse saindo armou) não vale para o aviso novo.
    window.clearTimeout(timer.current);
    if (!over.current) timer.current = window.setTimeout(hide, SHOW_MS);
    return () => window.clearTimeout(timer.current);
  }, [message, stamp]);

  // Com o aviso fora da tela, o mouse que estava em cima já não conta (não houve mouseleave).
  useEffect(() => {
    if (!montado) over.current = false;
  }, [montado]);

  if (!montado) return null;
  const swapped = firstStamp !== null && shown.stamp !== firstStamp;
  return createPortal(
    <div
      className={`rm-toast ds-toast${aberto ? ' is-aberto' : ''}`}
      role="status"
      aria-live="polite"
      onMouseEnter={() => {
        over.current = true;
        if (!open) return;
        window.clearTimeout(timer.current);
        left.current = Math.max(0, left.current - (Date.now() - startedAt.current));
      }}
      onMouseLeave={() => {
        over.current = false;
        if (!open) return;
        window.clearTimeout(timer.current);
        startedAt.current = Date.now();
        timer.current = window.setTimeout(hide, left.current);
      }}
    >
      <span key={shown.stamp} className={`rm-toast__body${swapped ? ' ds-aparece' : ''}`}>
        <span className="material-symbols-rounded" aria-hidden="true">
          {shown.icon}
        </span>
        {shown.message}
      </span>
      <span key={`t${shown.stamp}`} className="rm-toast__tempo ds-toast-tempo" aria-hidden="true" />
    </div>,
    document.body,
  );
}
